import * as Schema from "effect/Schema";
import { TrimmedNonEmptyString } from "./baseSchemas.ts";

/** Every item a Home suggestion stands for is a platform or workflow row with a UUID. */
const ItemId = Schema.String.check(
  Schema.isPattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
);

export const HomeSuggestionKind = Schema.Literals(["finding", "update", "workflow", "review"]);
export type HomeSuggestionKind = typeof HomeSuggestionKind.Type;

/**
 * The item a Home suggestion stands for: which row to read and what is asked of
 * it. The server reads the title itself, scoped to the organization; a browser
 * never sends one.
 *
 * - finding: an assessment (`PolicyImpactAssessment`) or a gap suggestion (`PolicyRecommendation`)
 * - update: a publication to assess (`RegulatoryChange`)
 * - workflow: a workflow whose last run or schedule failed
 * - review: a policy's review date (`policy`) or a publication's date (`change`)
 */
export const HomeSuggestionSubject = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("finding"),
    source: Schema.Literals(["assessment", "recommendation"]),
    id: ItemId,
  }),
  Schema.Struct({ kind: Schema.Literal("update"), id: ItemId }),
  Schema.Struct({ kind: Schema.Literal("workflow"), id: ItemId }),
  Schema.Struct({ kind: Schema.Literal("review"), source: Schema.Literals(["policy", "change"]), id: ItemId }),
]);
export type HomeSuggestionSubject = typeof HomeSuggestionSubject.Type;

/** Home shows three suggestions; a request names at most that many. */
export const HOME_SUGGESTION_LABEL_SUBJECTS_MAX = 3;

export const HomeSuggestionLabelsRequest = Schema.Struct({
  organizationId: TrimmedNonEmptyString.check(Schema.isMaxLength(128)),
  subjects: Schema.Array(HomeSuggestionSubject).check(
    Schema.isMaxLength(HOME_SUGGESTION_LABEL_SUBJECTS_MAX),
  ),
});
export type HomeSuggestionLabelsRequest = typeof HomeSuggestionLabelsRequest.Type;

/** Written labels only; a subject with none (gated, failed, over a ceiling, not found) is absent. */
export const HomeSuggestionLabelsResponse = Schema.Struct({
  labels: Schema.Array(
    Schema.Struct({ subject: HomeSuggestionSubject, label: TrimmedNonEmptyString.check(Schema.isMaxLength(64)) }),
  ),
});
export type HomeSuggestionLabelsResponse = typeof HomeSuggestionLabelsResponse.Type;
